import {
  Radio,
  ShieldAlert,
  Network,
  CheckCircle2,
  ArrowRight,
  Building2,
  Signal,
  Users,
  Globe2,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";

const TECH_DATA = {
  pt: {
    overline: "Qual tecnologia escolher?",
    title: "DMR, TETRA ou PoC — qual rádio é o certo para si?",
    subtitle:
      "Comparativo rápido das três principais tecnologias de radiocomunicação profissional que a TVCabSat representa em Angola.",
    labels: {
      best_for: "Ideal para",
      coverage: "Cobertura",
      strengths: "Pontos fortes",
      brands: "Marcas disponíveis",
      cta: "Ver equipamentos",
    },
    techs: [
      {
        id: "dmr",
        icon: "radio",
        name: "DMR",
        subtitle: "Digital Mobile Radio",
        badge: "Mais popular",
        accent: "#0284c7",
        tintBg: "bg-sky-50",
        tintBorder: "border-sky-200",
        tintText: "text-sky-700",
        tintCheck: "text-sky-600",
        badgeBg: "bg-sky-600",
        ctaBg: "bg-sky-600 hover:bg-sky-700",
        best_for: "Empresas, condomínios, indústria, logística, segurança privada, hotelaria.",
        coverage: "Rede privada via repetidores — cobre desde instalações locais a múltiplos sítios.",
        strengths: [
          "Voz digital clara com baixo ruído",
          "Custo operacional baixo (sem mensalidade)",
          "Comunicação instantânea (PTT)",
          "Modelos ATEX para áreas explosivas",
          "Investimento único em equipamento",
        ],
        brands: ["Motorola MOTOTRBO", "Hytera"],
        target: "motorola:mototrbo-portable",
      },
      {
        id: "tetra",
        icon: "shield",
        name: "TETRA",
        subtitle: "Trunked Digital (ETSI)",
        badge: "Missão crítica",
        accent: "#0055A4",
        tintBg: "bg-blue-50",
        tintBorder: "border-blue-200",
        tintText: "text-blue-700",
        tintCheck: "text-blue-600",
        badgeBg: "bg-blue-700",
        ctaBg: "bg-blue-700 hover:bg-blue-800",
        best_for: "Segurança pública, proteção civil, transporte público, utilities, aeroportos.",
        coverage: "Rede trunked dedicada multi-sítio com alta disponibilidade e redundância.",
        strengths: [
          "Certificação ETSI (segurança)",
          "Encriptação ponta a ponta",
          "Chamadas de grupo e individuais",
          "Prioridade e preempção de emergência",
          "Interoperabilidade entre agências",
        ],
        brands: ["Motorola APX", "Hytera PT590"],
        target: "motorola:apx-portable",
      },
      {
        id: "poc",
        icon: "network",
        name: "PoC / LTE",
        subtitle: "Push-to-Talk sobre Celular",
        badge: "Cobertura nacional",
        accent: "#0d9488",
        tintBg: "bg-teal-50",
        tintBorder: "border-teal-200",
        tintText: "text-teal-700",
        tintCheck: "text-teal-600",
        badgeBg: "bg-teal-600",
        ctaBg: "bg-teal-600 hover:bg-teal-700",
        best_for: "Frotas distribuídas, retalho multi-loja, operações nacionais, serviços com viaturas.",
        coverage: "Usa a rede móvel 4G/5G — cobertura em todo o país sem investir em infraestrutura.",
        strengths: [
          "Cobertura imediata em todo o país",
          "Sem necessidade de repetidores",
          "Voz + dados + vídeo + GPS",
          "Ecrã tátil e integração com apps",
          "Contratação por subscrição",
        ],
        brands: ["Hytera P50 Pro · PNC380"],
        target: "hytera:poc",
      },
    ],
  },
  en: {
    overline: "Which technology should you choose?",
    title: "DMR, TETRA or PoC — which radio is right for you?",
    subtitle:
      "Quick comparison of the three professional radio technologies TVCabSat represents in Angola.",
    labels: {
      best_for: "Ideal for",
      coverage: "Coverage",
      strengths: "Key strengths",
      brands: "Available brands",
      cta: "See equipment",
    },
    techs: [
      {
        id: "dmr",
        icon: "radio",
        name: "DMR",
        subtitle: "Digital Mobile Radio",
        badge: "Most popular",
        accent: "#0284c7",
        tintBg: "bg-sky-50",
        tintBorder: "border-sky-200",
        tintText: "text-sky-700",
        tintCheck: "text-sky-600",
        badgeBg: "bg-sky-600",
        ctaBg: "bg-sky-600 hover:bg-sky-700",
        best_for: "Businesses, condominiums, industry, logistics, private security, hospitality.",
        coverage: "Private network with repeaters — from local sites to multi-site deployments.",
        strengths: [
          "Clear digital voice, low noise",
          "Low running cost (no subscription)",
          "Instant push-to-talk (PTT)",
          "ATEX models for explosive areas",
          "One-off equipment investment",
        ],
        brands: ["Motorola MOTOTRBO", "Hytera"],
        target: "motorola:mototrbo-portable",
      },
      {
        id: "tetra",
        icon: "shield",
        name: "TETRA",
        subtitle: "Trunked Digital (ETSI)",
        badge: "Mission critical",
        accent: "#0055A4",
        tintBg: "bg-blue-50",
        tintBorder: "border-blue-200",
        tintText: "text-blue-700",
        tintCheck: "text-blue-600",
        badgeBg: "bg-blue-700",
        ctaBg: "bg-blue-700 hover:bg-blue-800",
        best_for: "Public safety, civil protection, public transport, utilities, airports.",
        coverage: "Dedicated multi-site trunked network with high availability and redundancy.",
        strengths: [
          "ETSI certification (security)",
          "End-to-end encryption",
          "Group and individual calls",
          "Emergency priority & preemption",
          "Inter-agency interoperability",
        ],
        brands: ["Motorola APX", "Hytera PT590"],
        target: "motorola:apx-portable",
      },
      {
        id: "poc",
        icon: "network",
        name: "PoC / LTE",
        subtitle: "Push-to-Talk over Cellular",
        badge: "Nationwide coverage",
        accent: "#0d9488",
        tintBg: "bg-teal-50",
        tintBorder: "border-teal-200",
        tintText: "text-teal-700",
        tintCheck: "text-teal-600",
        badgeBg: "bg-teal-600",
        ctaBg: "bg-teal-600 hover:bg-teal-700",
        best_for: "Distributed fleets, multi-store retail, nationwide ops, field services.",
        coverage: "Runs on 4G/5G mobile networks — nationwide coverage with no infrastructure invest.",
        strengths: [
          "Immediate nationwide coverage",
          "No repeaters required",
          "Voice + data + video + GPS",
          "Touchscreen and app integration",
          "Subscription-based contract",
        ],
        brands: ["Hytera P50 Pro · PNC380"],
        target: "hytera:poc",
      },
    ],
  },
  es: {
    overline: "¿Qué tecnología elegir?",
    title: "DMR, TETRA o PoC — ¿cuál radio es el adecuado?",
    subtitle:
      "Comparativo rápido de las tres tecnologías de radiocomunicación profesional que TVCabSat representa en Angola.",
    labels: {
      best_for: "Ideal para",
      coverage: "Cobertura",
      strengths: "Puntos fuertes",
      brands: "Marcas disponibles",
      cta: "Ver equipos",
    },
    techs: [
      {
        id: "dmr",
        icon: "radio",
        name: "DMR",
        subtitle: "Digital Mobile Radio",
        badge: "Más popular",
        accent: "#0284c7",
        tintBg: "bg-sky-50",
        tintBorder: "border-sky-200",
        tintText: "text-sky-700",
        tintCheck: "text-sky-600",
        badgeBg: "bg-sky-600",
        ctaBg: "bg-sky-600 hover:bg-sky-700",
        best_for: "Empresas, condominios, industria, logística, seguridad privada, hotelería.",
        coverage: "Red privada con repetidores — desde instalaciones locales a varios sitios.",
        strengths: [
          "Voz digital clara y sin ruido",
          "Coste operativo bajo (sin cuota)",
          "Comunicación instantánea (PTT)",
          "Modelos ATEX para áreas explosivas",
          "Inversión única en equipo",
        ],
        brands: ["Motorola MOTOTRBO", "Hytera"],
        target: "motorola:mototrbo-portable",
      },
      {
        id: "tetra",
        icon: "shield",
        name: "TETRA",
        subtitle: "Trunked Digital (ETSI)",
        badge: "Misión crítica",
        accent: "#0055A4",
        tintBg: "bg-blue-50",
        tintBorder: "border-blue-200",
        tintText: "text-blue-700",
        tintCheck: "text-blue-600",
        badgeBg: "bg-blue-700",
        ctaBg: "bg-blue-700 hover:bg-blue-800",
        best_for: "Seguridad pública, protección civil, transporte público, utilities, aeropuertos.",
        coverage: "Red trunked dedicada multi-sitio con alta disponibilidad y redundancia.",
        strengths: [
          "Certificación ETSI (seguridad)",
          "Cifrado extremo a extremo",
          "Llamadas de grupo e individuales",
          "Prioridad y preempción de emergencia",
          "Interoperabilidad entre agencias",
        ],
        brands: ["Motorola APX", "Hytera PT590"],
        target: "motorola:apx-portable",
      },
      {
        id: "poc",
        icon: "network",
        name: "PoC / LTE",
        subtitle: "Push-to-Talk sobre celular",
        badge: "Cobertura nacional",
        accent: "#0d9488",
        tintBg: "bg-teal-50",
        tintBorder: "border-teal-200",
        tintText: "text-teal-700",
        tintCheck: "text-teal-600",
        badgeBg: "bg-teal-600",
        ctaBg: "bg-teal-600 hover:bg-teal-700",
        best_for: "Flotas distribuidas, retail multi-tienda, operaciones nacionales, servicios con vehículos.",
        coverage: "Usa la red móvil 4G/5G — cobertura nacional sin invertir en infraestructura.",
        strengths: [
          "Cobertura nacional inmediata",
          "Sin necesidad de repetidores",
          "Voz + datos + video + GPS",
          "Pantalla táctil e integración con apps",
          "Contratación por suscripción",
        ],
        brands: ["Hytera P50 Pro · PNC380"],
        target: "hytera:poc",
      },
    ],
  },
};

const ICON_MAP = {
  radio: Radio,
  shield: ShieldAlert,
  network: Network,
};

const FOOTER_HINT = {
  pt: "Sem certeza qual a tecnologia certa? Fale com a nossa equipa.",
  en: "Not sure which technology you need? Talk to our team.",
  es: "¿No sabes cuál tecnología necesitas? Habla con nuestro equipo.",
};

export const RadioTechCompare = () => {
  const { lang } = useLang();
  // TECH_DATA is a module-level constant — direct lookup is referentially
  // stable across renders, no memoization needed.
  const data = TECH_DATA[lang] || TECH_DATA.pt;

  const goToTarget = (target) => {
    const nextHash = `#${target}`;
    if (window.location.hash === nextHash) {
      // Same hash → manually fire event so BrandShowcase re-applies
      window.dispatchEvent(new HashChangeEvent("hashchange"));
    } else {
      window.location.hash = nextHash;
    }
  };

  return (
    <section
      id="radio-compare"
      data-testid="radio-tech-compare"
      className="relative scroll-mt-24 overflow-hidden border-b border-slate-200 bg-gradient-to-b from-[#dfecf6] via-[#d8e8f5] to-[#d6e6f2] py-20 lg:py-24"
    >
      <div
        aria-hidden
        className="pointer-events-none absolute -top-40 right-1/3 h-[500px] w-[500px] rounded-full bg-blue-200/25 blur-3xl"
      />

      <div className="relative mx-auto max-w-[1400px] px-6 md:px-12 lg:px-16">
        <div className="max-w-3xl">
          <div className="font-mono text-[11px] uppercase tracking-[0.28em] text-blue-700">
            {data.overline}
          </div>
          <h2
            data-testid="radio-compare-title"
            className="font-heading mt-4 text-3xl font-black tracking-tight text-slate-900 md:text-4xl lg:text-5xl"
          >
            {data.title}
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-slate-600 md:text-base">
            {data.subtitle}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-3">
          {data.techs.map((tech) => {
            const Icon = ICON_MAP[tech.icon] || Radio;
            return (
              <article
                key={tech.id}
                data-testid={`tech-card-${tech.id}`}
                className={`group relative flex flex-col overflow-hidden rounded-3xl border bg-white p-6 shadow-sm transition-all hover:-translate-y-1 hover:shadow-xl md:p-7 ${tech.tintBorder}`}
              >
                {/* Accent top strip */}
                <div
                  aria-hidden
                  className="absolute inset-x-0 top-0 h-1"
                  style={{ background: tech.accent }}
                />

                <div className="flex items-start justify-between gap-3">
                  <div
                    className={`inline-flex h-12 w-12 items-center justify-center rounded-2xl ${tech.tintBg}`}
                  >
                    <Icon
                      className="h-6 w-6"
                      strokeWidth={2.2}
                      style={{ color: tech.accent }}
                    />
                  </div>
                  <span
                    className={`inline-flex items-center rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-white ${tech.badgeBg}`}
                  >
                    {tech.badge}
                  </span>
                </div>

                <h3 className="font-heading mt-5 text-2xl font-black text-slate-900 md:text-3xl">
                  {tech.name}
                </h3>
                <div
                  className={`mt-0.5 text-xs font-semibold uppercase tracking-wider ${tech.tintText}`}
                >
                  {tech.subtitle}
                </div>

                {/* Best for */}
                <div className="mt-6">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    <Users className="h-3 w-3" strokeWidth={2.5} />
                    {data.labels.best_for}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                    {tech.best_for}
                  </p>
                </div>

                {/* Coverage */}
                <div className="mt-5">
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    <Signal className="h-3 w-3" strokeWidth={2.5} />
                    {data.labels.coverage}
                  </div>
                  <p className="mt-1.5 text-sm leading-relaxed text-slate-700">
                    {tech.coverage}
                  </p>
                </div>

                {/* Strengths */}
                <div className="mt-5 flex-1">
                  <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    {data.labels.strengths}
                  </div>
                  <ul className="mt-2.5 space-y-1.5">
                    {tech.strengths.map((item) => (
                      <li
                        key={item}
                        className="flex items-start gap-2 text-sm text-slate-700"
                      >
                        <CheckCircle2
                          className={`mt-0.5 h-4 w-4 shrink-0 ${tech.tintCheck}`}
                          strokeWidth={2.5}
                        />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Brands */}
                <div className={`mt-6 rounded-2xl border p-3 ${tech.tintBorder} ${tech.tintBg}`}>
                  <div className="flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500">
                    <Building2 className="h-3 w-3" strokeWidth={2.5} />
                    {data.labels.brands}
                  </div>
                  <div className={`mt-1 text-sm font-semibold ${tech.tintText}`}>
                    {tech.brands.join(" · ")}
                  </div>
                </div>

                {/* CTA */}
                <button
                  type="button"
                  onClick={() => goToTarget(tech.target)}
                  data-testid={`tech-cta-${tech.id}`}
                  className={`mt-5 inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-md transition-all active:scale-95 ${tech.ctaBg}`}
                >
                  {data.labels.cta}
                  <ArrowRight
                    className="h-4 w-4 transition-transform group-hover:translate-x-0.5"
                    strokeWidth={2.5}
                  />
                </button>
              </article>
            );
          })}
        </div>

        <div className="mt-10 flex items-center justify-center gap-2 text-xs text-slate-500">
          <Globe2 className="h-3.5 w-3.5" strokeWidth={2} />
          <span>
            {FOOTER_HINT[lang] || FOOTER_HINT.pt}
          </span>
        </div>
      </div>
    </section>
  );
};

export default RadioTechCompare;
